import { useMemo, useState, useRef, useEffect } from "react";
import { connect, ConnectedProps } from "react-redux";
import { AppDispatch, RootState } from "store";
import { EditPreliquidationAmount } from "./EditPreliquidationAmount";
import {
  EditPreliquidationAmountContainerProps,
  PreliquidationAmountForm,
} from "./types";
import {
  actions as preliActions,
  preliquidationSelector,
} from "reducers/preliquidation";
import { AdjustAmountMiddlewareType } from "sagas/types/preliquidation";
import { LoadingButtonState } from "component/loadingButton/types";
import { validationSchema } from "./schema";
import { FormRefType } from "utils/types";

const EditPreliquidationAmountContainer = (
  props: EditPreliquidationAmountContainerProps & ConnectorProps
): JSX.Element => {
  const formRef: FormRefType<PreliquidationAmountForm> = useRef(null);
  const [increase, setIncrease] = useState(true);
  const { preliquidation } = props;

  const initialValues: PreliquidationAmountForm = useMemo(() => {
    return {
      actualAmount: `$${preliquidation.total}`,
      newAmount: "",
      reason: "",
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preliquidation]);

  const onClose = (reload?: boolean) => {
    props.onCloseModal();
    setIncrease(true);
    if (reload && preliquidation.id)
      props.getDetailPreliquidation(preliquidation.id);
  };

  const onSubmit = (values: PreliquidationAmountForm) => {
    // Evita multiples llamados
    if (props.adjustingAmount > LoadingButtonState.Idle) return;
    const params: AdjustAmountMiddlewareType = {
      id: preliquidation.id,
      currentAmount: preliquidation.total,
      callback: props.getDetailPreliquidation,
      adjustment: {
        amount: Number(`${!increase ? "-" : ""}${values.newAmount}`),
        reason: values.reason,
      },
    };
    props.adjustAmount(params);
  };

  const schema = useMemo(() => {
    return validationSchema(increase, preliquidation.manualCorrection);
  }, [increase, preliquidation.manualCorrection]);

  useEffect(() => {
    if (formRef.current) formRef.current.restart();
  }, [props.showModal]);

  return (
    <EditPreliquidationAmount
      {...props}
      formRef={formRef}
      onClose={onClose}
      isOpen={props.showModal}
      increase={increase}
      setIncrease={setIncrease}
      validationSchema={schema}
      initialValues={initialValues}
      onSubmit={onSubmit}
      testId="edit-preliquidation-amount"
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  showModal: preliquidationSelector(state).showEditPreliquidationModal,
  adjustingAmount: preliquidationSelector(state).adjustingAmount,
  preliquidation: preliquidationSelector(state).detailPreliquidations,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onCloseModal: () => {
    dispatch(preliActions.toggleModalVisibility(false));
  },
  getDetailPreliquidation: (id: number) => {
    dispatch(preliActions.getDetailPreliquidationsRequest(id));
  },
  adjustAmount: (params: AdjustAmountMiddlewareType) => {
    dispatch(preliActions.adjustAmountFetch(params));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(EditPreliquidationAmountContainer);
